import { useState, useEffect } from 'react';

import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { GetStaticProps } from 'next';
import { useInfiniteQuery } from 'react-query';

import { Seo, Container, Button } from '~/components';
import { BannerDynamic } from '~/components/Banners/BannerDynamic';
import { ArticleCard, BlogHeader, LatestArticles } from '~/components/Blog';
import { storyblokSdk } from '~/graphql/client';
import { BlogpostItems } from '~/graphql/sdk';
import { addFormFieldsToStory, enhancedStaticProps, withLayout } from '~/lib';

export interface BlogProps extends WithStoryProps {
  blogItems?: BlogpostItems;
  categoryItems?: any[];
}

const Blog = ({ blogItems, categoryItems, story }: BlogProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [category, setCategory] = useState<string>('all');

  const featuredAll = blogItems?.items?.slice(0, 2);
  const [latestArticles, setLatestArticles] = useState<any[]>(featuredAll);

  useEffect(() => {
    if (category === 'all') {
      setCurrentPage(1);
      setLatestArticles(featuredAll);
    } else {
      setCurrentPage(1);

      const currentItems = categoryItems?.find(
        (item) => item?.category === category,
      );

      setLatestArticles(currentItems?.items?.slice(0, 2));
    }
  }, [category]);

  const getMoreArticles = async ({ category, pageParam = 1 }) => {
    try {
      const articles = (
        await storyblokSdk.blogPostItems({
          filterId: String(story?.content?.featured_post?.id),
          perPage: 8,
          page: pageParam,
          filterQuery:
            category === 'all' ? undefined : { category: { like: category } },
        })
      ).BlogpostItems;

      return pageParam === 1
        ? { ...articles, items: articles?.items?.slice(2, 8) }
        : articles;
    } catch (e) {
      console.log(e);
    }
  };

  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ['blogPostItems', category],
    ({ pageParam }) => getMoreArticles({ category, pageParam }),
    {
      getNextPageParam: (lastPage, pages) =>
        lastPage?.total > pages?.length * 8 ? pages.length + 1 : undefined,
    },
  );

  const categoryData = data?.pages[0]?.items
    ?.map((item) => item?.content?.category)
    ?.every((cat) => cat === category);

  const paginatedData = data?.pages?.map((page) => page?.items);

  const currentCategoryItems = categoryItems
    ?.find((cat) => category === cat?.category)
    ?.items?.slice(2, 8);

  const moreArticles =
    category === 'all'
      ? paginatedData || [blogItems?.items?.slice(2, 8)]
      : categoryData
      ? paginatedData
      : [currentCategoryItems];

  return (
    <>
      <Seo story={story} />
      <Container first className="lg:px-5">
        <BlogHeader
          title={story?.content?.title}
          totalArticles={blogItems?.total}
          banner={story?.content?.banner_first?.content}
        />

        {!!story?.content?.featured_post && (
          <ArticleCard article={story.content.featured_post} featured />
        )}

        <LatestArticles
          title={story?.content?.latest_articles_title}
          featuredItems={latestArticles}
          categories={categoryItems?.map((item) => item?.category)}
          category={category}
          setCategory={setCategory}
        />
        {/* {story?.content?.banner_first?.content && (
          <div className="mt-32">
            <BannerDynamic content={story.content.banner_first.content} />
          </div>
        )} */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14 md:gap-y-[72px]">
          {!!blogItems?.items &&
            moreArticles?.map((page) =>
              page.map((article) => (
                <ArticleCard key={article?.uuid} article={article} grid />
              )),
            )}
        </div>
        {moreArticles[0]?.length > 0 && (
          <div className="w-full flex justify-center mt-20">
            <Button
              variant="secondary"
              size="large"
              disabled={!hasNextPage}
              isLoading={isFetchingNextPage}
              onClick={async () => {
                await fetchNextPage({ pageParam: currentPage + 1 });
                setCurrentPage(currentPage + 1);
              }}
            >
              Load more articles
            </Button>
          </div>
        )}
        {story?.content?.banner_last?.content && (
          <div className="mt-32">
            <BannerDynamic content={story.content.banner_last.content} />
          </div>
        )}
      </Container>
    </>
  );
};

export default withLayout(withStory(Blog));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ sdk }) => {
    let story;
    let blogItems;
    let categoryItems;
    let notFound = false;

    try {
      story = (await sdk.blogOverviewItem({ slug: 'blog' })).BlogoverviewItem;
      story = await addFormFieldsToStory(story);
    } catch (e) {
      notFound = true;
    }

    if (story?.content?.featured_post?.id) {
      try {
        blogItems = (
          await sdk.blogPostItems({
            filterId: String(story?.content?.featured_post?.id),
            perPage: 8,
          })
        ).BlogpostItems;
      } catch (e) {
        notFound = true;
      }
    } else {
      try {
        blogItems = (
          await sdk.blogPostItems({
            filterId: '',
          })
        ).BlogpostItems;
      } catch (e) {
        notFound = true;
      }
    }

    const categories = ['eCommerce', 'Product', 'Company'];

    try {
      categoryItems = await Promise.all(
        categories.map(async (cat) => {
          const featured = (
            await sdk.blogPostItems({
              filterQuery: { category: { like: cat } },
              perPage: 8,
            })
          ).BlogpostItems;

          return { category: cat, ...featured };
        }),
      );
    } catch (e) {
      console.log(e);
    }

    return {
      props: {
        story,
        blogItems,
        categoryItems,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);
